/** Developer/admin inspector for publish JobRuns.
 *
 *  Surfaces three things the broker-facing panels don't:
 *    1. A 30-day historical window with structured filters (the bulk
 *       panel caps at 24h and only filters by viewer's own).
 *    2. Aggregate stats — today's volume, success rate, avg duration,
 *       top error messages — so the operator can spot pattern shifts
 *       (CDN flake, SS draft churn, MyHome rate-limits).
 *    3. Full result-payload + events stream for a single run, plus the
 *       supersede chain for retries.
 *
 *  Page-gated by the backend's ``JobsAdminUser`` dep (owner+director by
 *  default; tweak in Role permissions). The route uses ``PageGuard``
 *  with the same page key so the frontend doesn't render a useless
 *  flash of "loading…" for users that aren't allowed in.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import { Avatar } from "@/components/Avatar";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";

type JobStatus = "pending" | "running" | "done" | "error" | "cancelled";
type Platform = "myhome" | "ss" | "both" | "none";

interface PlatformInfo {
  attempted: boolean;
  listing_id?: string | null;
  application_id?: number | null;
  ok: boolean;
  error?: string | null;
}

interface JobRow {
  id: string;
  status: JobStatus;
  source_url: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  error_message: string | null;
  agent: { id: string | null; name: string | null; avatar_url: string | null };
  triggered_by: {
    id: string | null;
    name: string | null;
    avatar_url: string | null;
  };
  platforms: { myhome: PlatformInfo; ss: PlatformInfo };
  placement_count: number;
  in_memory_job_id: string | null;
  superseded_by_id: string | null;
}

interface JobsResponse {
  items: JobRow[];
  total: number;
  limit: number;
  offset: number;
  window: { from: string; to: string };
}

interface StatsResponse {
  window: { from: string; to: string };
  totals: {
    all: number;
    done: number;
    error: number;
    cancelled: number;
    in_flight: number;
  };
  today: { total: number; errors: number };
  success_rate_pct: number | null;
  avg_duration_ms: number | null;
  top_errors: Array<{ message: string; count: number }>;
  by_agent: Array<{
    user_id: string | null;
    name: string | null;
    avatar_url: string | null;
    total: number;
    errors: number;
  }>;
}

interface PlacementSummary {
  id: string;
  platform: string;
  external_id: string | null;
  published_url: string;
  title: string | null;
  published_at: string | null;
}

interface JobDetail extends JobRow {
  result: Record<string, unknown> | null;
  events: Array<{ step?: string; status?: string; message?: string }>;
  placements: PlacementSummary[];
  predecessor_id: string | null;
  use_ss_live: boolean | null;
  stop_requested: boolean;
  pause_requested: boolean;
}

const STATUS_OPTIONS: { key: JobStatus | ""; label: string }[] = [
  { key: "", label: "All" },
  { key: "running", label: "Running" },
  { key: "done", label: "Done" },
  { key: "error", label: "Failed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "pending", label: "Pending" },
];

const PLATFORM_OPTIONS: { key: Platform | ""; label: string }[] = [
  { key: "", label: "Any" },
  { key: "both", label: "MyHome + SS" },
  { key: "myhome", label: "MyHome only" },
  { key: "ss", label: "SS only" },
];

const PAGE_SIZE = 50;

export function AdminJobs() {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [statsErr, setStatsErr] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<JobStatus | "">("");
  const [platformFilter, setPlatformFilter] = useState<Platform | "">("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [includeSuperseded, setIncludeSuperseded] = useState(false);

  const [rows, setRows] = useState<JobRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [listErr, setListErr] = useState<string | null>(null);
  const [loadingRows, setLoadingRows] = useState(false);

  const [openId, setOpenId] = useState<string | null>(null);

  // Auto-refresh every 15s when at offset 0 — keeps the panel live for
  // anyone watching jobs land without hammering the API. Pause when
  // the user paginates back so they don't get yanked off the page mid-read.
  const refreshTokenRef = useRef(0);
  const reloadList = useCallback(async () => {
    setLoadingRows(true);
    const token = ++refreshTokenRef.current;
    try {
      const qs = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (statusFilter) qs.set("status", statusFilter);
      if (platformFilter) qs.set("platform", platformFilter);
      if (search.trim()) qs.set("q", search.trim());
      if (includeSuperseded) qs.set("include_superseded", "true");
      const r = await api<JobsResponse>(`/api/admin/jobs?${qs}`);
      // Drop stale responses — if the user changed a filter while a
      // slow request was in flight, the newer reload's token will be
      // higher than ours.
      if (token !== refreshTokenRef.current) return;
      setRows(r.items);
      setTotal(r.total);
      setListErr(null);
    } catch (e) {
      if (token !== refreshTokenRef.current) return;
      setListErr(extractErrMsg(e));
      setRows([]);
    } finally {
      if (token === refreshTokenRef.current) setLoadingRows(false);
    }
  }, [statusFilter, platformFilter, search, includeSuperseded, offset]);

  const reloadStats = useCallback(async () => {
    try {
      const s = await api<StatsResponse>("/api/admin/jobs/stats");
      setStats(s);
      setStatsErr(null);
    } catch (e) {
      setStatsErr(extractErrMsg(e));
    }
  }, []);

  useEffect(() => {
    reloadList();
  }, [reloadList]);
  useEffect(() => {
    reloadStats();
  }, [reloadStats]);

  useEffect(() => {
    if (offset !== 0) return;
    const t = window.setInterval(() => {
      reloadList();
      reloadStats();
    }, 15_000);
    return () => window.clearInterval(t);
  }, [offset, reloadList, reloadStats]);

  // Debounce the search input so we don't fire a query on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput);
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const hasMore = offset + (rows?.length ?? 0) < total;

  return (
    <div className="w-full space-y-4">
      <PageHeader
        title="Job runs"
        subtitle="Developer inspector for the publishing pipeline — last 30 days."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                reloadList();
                reloadStats();
              }}
              className="h-9 px-3 rounded-lg bg-ink-900/60 border border-white/10 text-[12px] text-slate-300 hover:text-white hover:border-white/20 transition"
              disabled={loadingRows}
            >
              {loadingRows ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        }
      />

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile
          label="Today"
          value={stats?.today.total}
          hint={
            stats?.today.errors !== undefined
              ? `${stats.today.errors} failed`
              : undefined
          }
          tone={
            stats && stats.today.errors > 0 ? "rose" : "neutral"
          }
        />
        <StatTile
          label="Success rate · 30d"
          value={
            stats?.success_rate_pct == null
              ? "—"
              : `${stats.success_rate_pct}%`
          }
          hint={
            stats
              ? `${stats.totals.done} of ${stats.totals.all}`
              : undefined
          }
          tone={
            stats && stats.success_rate_pct != null
              ? stats.success_rate_pct >= 95
                ? "emerald"
                : stats.success_rate_pct >= 80
                  ? "amber"
                  : "rose"
              : "neutral"
          }
        />
        <StatTile
          label="Avg duration · 30d"
          value={formatDuration(stats?.avg_duration_ms ?? null)}
          hint="finished runs only"
        />
        <StatTile
          label="In-flight"
          value={stats?.totals.in_flight}
          hint="pending + running"
          tone={
            stats && stats.totals.in_flight > 0 ? "accent" : "neutral"
          }
        />
      </div>

      {statsErr && (
        <div className="card border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-xs p-3">
          Stats: {statsErr}
        </div>
      )}

      {/* Top errors + by-agent — two compact strips so the operator
          gets a "what went wrong / who's pushing the volume" snapshot
          without opening any drawer. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="card p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Top errors · 30d
          </div>
          {!stats ? (
            <Skeleton className="h-20" />
          ) : stats.top_errors.length === 0 ? (
            <div className="text-[12.5px] text-slate-500">
              No errors in window.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {stats.top_errors.map((e, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[12.5px]"
                >
                  <span className="text-slate-500 tabular-nums w-8 shrink-0">
                    ×{e.count}
                  </span>
                  <span className="text-slate-200 break-words flex-1">
                    {e.message}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card p-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            By agent · 30d
          </div>
          {!stats ? (
            <Skeleton className="h-20" />
          ) : stats.by_agent.length === 0 ? (
            <div className="text-[12.5px] text-slate-500">
              No runs in window.
            </div>
          ) : (
            <ul className="space-y-1.5">
              {stats.by_agent.map((row, i) => (
                <li
                  key={row.user_id ?? `i-${i}`}
                  className="flex items-center gap-2 text-[12.5px]"
                >
                  <Avatar
                    url={row.avatar_url}
                    name={row.name}
                    email={null}
                    size="xs"
                  />
                  <span className="text-slate-200 flex-1 truncate">
                    {row.name || "—"}
                  </span>
                  <span className="text-slate-300 tabular-nums">
                    {row.total}
                  </span>
                  {row.errors > 0 && (
                    <span className="text-rose-400 tabular-nums text-[11px]">
                      ({row.errors} err)
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-3">
        <div className="flex items-center flex-wrap gap-2">
          <ChipGroup
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v as JobStatus | "");
              setOffset(0);
            }}
            options={STATUS_OPTIONS}
          />
          <ChipGroup
            value={platformFilter}
            onChange={(v) => {
              setPlatformFilter(v as Platform | "");
              setOffset(0);
            }}
            options={PLATFORM_OPTIONS}
          />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search source URL or error…"
            className="h-9 px-3 rounded-lg bg-ink-900/60 border border-white/10 text-[12px] text-slate-100 focus:outline-none focus:border-accent-500/60 focus:ring-2 focus:ring-accent-500/20 min-w-[220px] flex-1"
          />
          <label className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-400 hover:text-slate-200 select-none cursor-pointer px-2">
            <input
              type="checkbox"
              checked={includeSuperseded}
              onChange={(e) => {
                setIncludeSuperseded(e.target.checked);
                setOffset(0);
              }}
              className="accent-accent-500"
            />
            Include superseded retries
          </label>
        </div>
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        {listErr && (
          <div className="border-b border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-xs px-4 py-2">
            {listErr}
          </div>
        )}
        {/* Mobile rows */}
        <div className="md:hidden divide-y divide-white/5">
          {rows === null ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-[12.5px] text-slate-500 text-center">
              No jobs match these filters.
            </div>
          ) : (
            rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setOpenId(r.id)}
                className="w-full text-left p-3 hover:bg-white/[0.02] transition"
              >
                <div className="flex items-center gap-2 mb-1">
                  <StatusBadge status={r.status} />
                  <span className="text-[11.5px] text-slate-500 tabular-nums">
                    {formatTime(r.started_at)}
                  </span>
                </div>
                <div className="text-[12.5px] text-slate-200 truncate">
                  {r.source_url}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Avatar
                    url={r.agent.avatar_url}
                    name={r.agent.name}
                    email={null}
                    size="xs"
                  />
                  <span className="text-[11px] text-slate-400 truncate">
                    {r.agent.name || "—"}
                  </span>
                  <PlatformChips row={r} />
                </div>
              </button>
            ))
          )}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
              <tr>
                <th className="text-left px-4 py-2">Started</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Source</th>
                <th className="text-left px-4 py-2">Agent</th>
                <th className="text-left px-4 py-2">Triggered by</th>
                <th className="text-left px-4 py-2">Platforms</th>
                <th className="text-right px-4 py-2">Duration</th>
                <th className="text-right px-4 py-2">PL</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td colSpan={9} className="px-4 py-2">
                      <Skeleton className="h-6" />
                    </td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-8 text-center text-[12.5px] text-slate-500"
                  >
                    No jobs match these filters.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => setOpenId(r.id)}
                    className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                  >
                    <td className="px-4 py-1.5 text-slate-400 tabular-nums whitespace-nowrap">
                      {formatTime(r.started_at)}
                    </td>
                    <td className="px-4 py-1.5">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-1.5 text-slate-200 max-w-[280px]">
                      <span className="block truncate" title={r.source_url}>
                        {r.source_url}
                      </span>
                      {r.error_message && (
                        <span
                          className="block text-[10.5px] text-rose-400/90 truncate"
                          title={r.error_message}
                        >
                          {r.error_message.split("\n")[0]}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-1.5">
                      <UserChip user={r.agent} />
                    </td>
                    <td className="px-4 py-1.5">
                      {r.triggered_by.id &&
                      r.triggered_by.id !== r.agent.id ? (
                        <UserChip user={r.triggered_by} />
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-1.5">
                      <PlatformChips row={r} />
                    </td>
                    <td className="px-4 py-1.5 text-right text-slate-400 tabular-nums whitespace-nowrap">
                      {formatDuration(r.duration_ms)}
                    </td>
                    <td className="px-4 py-1.5 text-right text-slate-400 tabular-nums">
                      {r.placement_count || ""}
                    </td>
                    <td className="px-4 py-1.5 text-right text-slate-500">
                      →
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pager */}
        {rows && total > PAGE_SIZE && (
          <div className="border-t border-white/5 px-4 py-2.5 flex items-center justify-between text-[11.5px] text-slate-400">
            <span>
              {offset + 1}–{offset + rows.length} of {total}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  setOffset(Math.max(0, offset - PAGE_SIZE))
                }
                disabled={offset === 0}
                className="px-2.5 py-1 rounded border border-white/10 hover:border-white/20 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={!hasMore}
                className="px-2.5 py-1 rounded border border-white/10 hover:border-white/20 hover:text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {openId && (
        <JobDetailDrawer
          jobId={openId}
          onClose={() => setOpenId(null)}
          onChange={() => {
            reloadList();
            reloadStats();
          }}
        />
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: number | string | undefined;
  hint?: string;
  tone?: "neutral" | "accent" | "emerald" | "amber" | "rose";
}) {
  const TONE: Record<NonNullable<typeof tone>, string> = {
    neutral: "text-slate-100",
    accent: "text-accent-300",
    emerald: "text-emerald-300",
    amber: "text-amber-300",
    rose: "text-rose-300",
  };
  return (
    <div className="card p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div
        className={`text-[22px] font-semibold tabular-nums leading-tight mt-1 ${TONE[tone]}`}
      >
        {value === undefined ? <Skeleton className="h-6 w-16" /> : value}
      </div>
      {hint && (
        <div className="text-[10.5px] text-slate-500 mt-0.5">{hint}</div>
      )}
    </div>
  );
}

function ChipGroup<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T | "";
  onChange: (next: T | "") => void;
  options: { key: T | ""; label: string }[];
}) {
  return (
    <div className="inline-flex bg-ink-900/60 border border-white/10 rounded-lg p-0.5">
      {options.map((o) => (
        <button
          key={o.key || "all"}
          type="button"
          onClick={() => onChange(o.key)}
          className={`px-2.5 py-1 rounded text-[11.5px] transition ${
            value === o.key
              ? "bg-accent-500/20 text-accent-200"
              : "text-slate-400 hover:text-slate-200"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const STATUS_STYLE: Record<JobStatus, string> = {
  pending: "bg-slate-500/15 text-slate-300 border border-slate-500/30",
  running: "bg-accent-500/15 text-accent-200 border border-accent-500/30",
  done: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  error: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  cancelled: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
};

function StatusBadge({ status }: { status: JobStatus }) {
  return (
    <span
      className={`inline-flex items-center px-1.5 py-[1px] rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_STYLE[status]}`}
    >
      {status}
    </span>
  );
}

function PlatformChips({ row }: { row: JobRow }) {
  return (
    <div className="inline-flex items-center gap-1">
      {row.platforms.myhome.attempted && (
        <PlatformPip
          label="MH"
          ok={row.platforms.myhome.ok}
          href={
            row.platforms.myhome.listing_id
              ? `https://www.myhome.ge/ka/pr/${row.platforms.myhome.listing_id}/`
              : null
          }
        />
      )}
      {row.platforms.ss.attempted && (
        <PlatformPip
          label="SS"
          ok={row.platforms.ss.ok}
          href={
            row.platforms.ss.application_id
              ? `https://home.ss.ge/ka/udzravi-qoneba/${row.platforms.ss.application_id}`
              : null
          }
        />
      )}
    </div>
  );
}

function PlatformPip({
  label,
  ok,
  href,
}: {
  label: string;
  ok: boolean;
  href: string | null;
}) {
  const cls = ok
    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25"
    : "bg-rose-500/15 text-rose-300 border-rose-500/30";
  if (ok && href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`inline-flex items-center px-1.5 py-[1px] rounded text-[10px] font-semibold border ${cls} transition`}
      >
        {label}
      </a>
    );
  }
  return (
    <span
      className={`inline-flex items-center px-1.5 py-[1px] rounded text-[10px] font-semibold border ${cls}`}
    >
      {label}
    </span>
  );
}

function UserChip({
  user,
}: {
  user: { id: string | null; name: string | null; avatar_url: string | null };
}) {
  if (!user.id) return <span className="text-slate-600">—</span>;
  return (
    <div className="inline-flex items-center gap-1.5">
      <Avatar
        url={user.avatar_url}
        name={user.name}
        email={null}
        size="xs"
      />
      <span className="text-slate-300 max-w-[140px] truncate">
        {user.name || "—"}
      </span>
    </div>
  );
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function extractErrMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Request failed";
}

function JobDetailDrawer({
  jobId,
  onClose,
  onChange,
}: {
  jobId: string;
  onClose: () => void;
  onChange: () => void;
}) {
  const [detail, setDetail] = useState<JobDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api<JobDetail>(`/api/admin/jobs/${jobId}`);
      setDetail(d);
      setErr(null);
    } catch (e) {
      setErr(extractErrMsg(e));
    }
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  // Live-refresh while the job is in-flight so events stream in.
  useEffect(() => {
    if (!detail || (detail.status !== "running" && detail.status !== "pending"))
      return;
    const t = window.setInterval(load, 5000);
    return () => window.clearInterval(t);
  }, [detail, load]);

  // Esc closes — modal hygiene.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const act = async (
    label: string,
    fn: () => Promise<unknown>,
  ) => {
    setBusy(label);
    try {
      await fn();
      onChange();
      await load();
    } catch (e) {
      setErr(extractErrMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/55"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-ink-900 border-l border-white/10 w-full max-w-2xl h-full overflow-y-auto">
        <div className="sticky top-0 z-10 bg-ink-900/95 backdrop-blur border-b border-white/5 px-5 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              Job run
            </div>
            <div className="text-[12px] text-slate-300 font-mono truncate">
              {jobId}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white text-lg leading-none px-2"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {err && (
          <div className="m-4 card border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-xs p-3">
            {err}
          </div>
        )}

        {!detail ? (
          <div className="p-5 space-y-3">
            <Skeleton className="h-16" />
            <Skeleton className="h-32" />
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={detail.status} />
              <PlatformChips row={detail} />
              {detail.in_memory_job_id && (
                <span className="text-[10px] uppercase tracking-wider text-accent-300 border border-accent-500/30 bg-accent-500/10 px-1.5 py-[1px] rounded">
                  worker alive
                </span>
              )}
              {detail.superseded_by_id && (
                <Link
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    // Open the successor in the same drawer.
                    window.location.hash = "";
                    onClose();
                    setTimeout(() => {
                      // Caller (AdminJobs) sets openId via setOpenId — we
                      // can't reach it directly from here. Easiest is a
                      // page-level URL hop; for now, surface as text.
                    }, 0);
                  }}
                  className="text-[10.5px] text-amber-300 underline-offset-2 hover:underline"
                  title={detail.superseded_by_id}
                >
                  superseded by {detail.superseded_by_id.slice(0, 8)}…
                </Link>
              )}
              {detail.predecessor_id && (
                <span
                  className="text-[10.5px] text-slate-400"
                  title={detail.predecessor_id}
                >
                  replaces {detail.predecessor_id.slice(0, 8)}…
                </span>
              )}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Source
              </div>
              <a
                href={detail.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[12.5px] text-accent-300 hover:text-accent-200 break-all"
              >
                {detail.source_url} ↗
              </a>
            </div>

            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <KV label="Started" value={formatTime(detail.started_at)} />
              <KV label="Finished" value={formatTime(detail.finished_at)} />
              <KV
                label="Duration"
                value={formatDuration(detail.duration_ms)}
              />
              <KV label="Placements" value={String(detail.placement_count)} />
            </div>

            {detail.error_message && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-rose-400 font-semibold mb-1">
                  Error
                </div>
                <pre className="bg-rose-500/[0.06] border border-rose-500/20 rounded p-3 text-[12px] text-rose-200 whitespace-pre-wrap break-words">
                  {detail.error_message}
                </pre>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center flex-wrap gap-2 pt-1">
              {(detail.status === "running" ||
                detail.status === "pending") &&
                detail.in_memory_job_id && (
                  <button
                    type="button"
                    onClick={() =>
                      act("stop", () =>
                        api(`/api/job-runs/${detail.id}/stop`, {
                          method: "POST",
                        }),
                      )
                    }
                    disabled={busy !== null}
                    className="text-[11.5px] px-2.5 py-1 rounded-md bg-rose-600/80 hover:bg-rose-500 text-white transition-colors disabled:opacity-50"
                  >
                    {busy === "stop" ? "Stopping…" : "Stop"}
                  </button>
                )}
              {detail.status === "error" && (
                <>
                  {!detail.platforms.myhome.ok && (
                    <button
                      type="button"
                      onClick={() =>
                        act("rmh", () =>
                          api(
                            `/api/job-runs/${detail.id}/retry-platform`,
                            { method: "POST", json: { platform: "myhome" } },
                          ),
                        )
                      }
                      disabled={busy !== null}
                      className="text-[11.5px] px-2.5 py-1 rounded-md bg-accent-600 hover:bg-accent-500 text-white transition-colors disabled:opacity-50"
                    >
                      {busy === "rmh" ? "Retrying…" : "Retry MyHome"}
                    </button>
                  )}
                  {detail.platforms.ss.attempted &&
                    !detail.platforms.ss.ok && (
                      <button
                        type="button"
                        onClick={() =>
                          act("rss", () =>
                            api(
                              `/api/job-runs/${detail.id}/retry-platform`,
                              { method: "POST", json: { platform: "ss" } },
                            ),
                          )
                        }
                        disabled={busy !== null}
                        className="text-[11.5px] px-2.5 py-1 rounded-md bg-accent-600 hover:bg-accent-500 text-white transition-colors disabled:opacity-50"
                      >
                        {busy === "rss" ? "Retrying…" : "Retry SS"}
                      </button>
                    )}
                </>
              )}
            </div>

            {detail.placements.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  Placements created
                </div>
                <ul className="divide-y divide-white/5 border border-white/5 rounded">
                  {detail.placements.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center gap-3 px-3 py-2 text-[12px]"
                    >
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 w-12 shrink-0">
                        {p.platform}
                      </span>
                      <span className="font-mono tabular-nums text-slate-300 shrink-0">
                        {p.external_id || "—"}
                      </span>
                      <a
                        href={p.published_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-300 hover:text-accent-200 truncate flex-1"
                      >
                        {p.title || p.published_url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {detail.events.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                  Events ({detail.events.length})
                </div>
                <ul className="space-y-0.5 max-h-72 overflow-y-auto border border-white/5 rounded p-2 bg-black/20 font-mono text-[11px]">
                  {detail.events.map((e, i) => {
                    const color =
                      e.status === "error"
                        ? "text-rose-300"
                        : e.status === "done"
                          ? "text-emerald-300"
                          : "text-slate-300";
                    return (
                      <li
                        key={i}
                        className={`whitespace-pre-wrap break-words ${color}`}
                      >
                        <span className="text-slate-500">[{e.step || "?"}]</span>{" "}
                        {e.message || ""}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => setShowJson((v) => !v)}
                className="text-[11.5px] text-slate-400 hover:text-slate-200"
              >
                {showJson ? "▾ Hide" : "▸ Show"} full result JSON
              </button>
              {showJson && (
                <pre className="mt-2 bg-black/40 border border-white/5 rounded p-3 text-[10.5px] text-slate-300 overflow-x-auto max-h-96">
                  {JSON.stringify(detail.result ?? {}, null, 2)}
                </pre>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div className="text-slate-200 tabular-nums">{value}</div>
    </div>
  );
}
